import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressBarModule } from 'primeng/progressbar';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import {
  OpportunityItem,
  OpportunityStatus,
  OpportunityUpdateRequest,
} from '../models/opportunity.models';
import { OpportunitiesApiService } from '../opportunities-api.service';

@Component({
  selector: 'gig-opportunity-detail-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SidebarModule,
    ButtonModule,
    DropdownModule,
    ProgressBarModule,
    CalendarModule,
    InputTextModule,
    TagModule,
    ToastModule,
    TooltipModule,
  ],
  templateUrl: './opportunity-detail-panel.component.html',
  styleUrls: ['./opportunity-detail-panel.component.scss'],
  providers: [MessageService],
})
export class OpportunityDetailPanelComponent implements OnChanges {
  @Input() public visible = false;
  @Input() public opportunity: OpportunityItem | null = null;
  @Input() public canEdit = false;

  @Output() public visibleChange = new EventEmitter<boolean>();
  @Output() public save = new EventEmitter<{ item: OpportunityItem; request: OpportunityUpdateRequest }>();
  @Output() public summaryRegenerated = new EventEmitter<OpportunityItem>();

  public editStatus: OpportunityStatus | null = null;
  public editAssignedTo = '';
  public editDueDate: Date | null = null;
  public isRegeneratingLocal = false;

  public readonly statusOptions: { label: string; value: OpportunityStatus }[] = [
    { label: 'New', value: 'NEW' },
    { label: 'Accepted', value: 'ACCEPTED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Done', value: 'DONE' },
    { label: 'Ignored', value: 'IGNORED' },
  ];

  constructor(private apiService: OpportunitiesApiService, private messageService: MessageService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['opportunity'] && this.opportunity) {
      this.editStatus = this.opportunity.status;
      this.editAssignedTo = this.opportunity.assignedTo ?? '';
      this.editDueDate = this.opportunity.dueDate ? new Date(this.opportunity.dueDate) : null;
    }
  }

  public onHide(): void {
    this.visibleChange.emit(false);
  }

  public onSave(): void {
    if (!this.opportunity) {
      return;
    }
    const request: OpportunityUpdateRequest = {
      status: this.editStatus ?? undefined,
      assignedTo: this.editAssignedTo || undefined,
      dueDate: this.editDueDate ? this.editDueDate.toISOString() : undefined,
    };
    this.save.emit({ item: this.opportunity, request });
  }

  public getSignalStrength(): number {
    return this.opportunity ? Math.round(this.opportunity.confidence * 100) : 0;
  }

  public getEffortSeverity(): 'success' | 'warning' | 'danger' | 'info' {
    if (!this.opportunity) {
      return 'info';
    }
    const map: Record<string, 'success' | 'warning' | 'danger'> = {
      LOW: 'success',
      MEDIUM: 'warning',
      HIGH: 'danger',
    };
    return map[this.opportunity.effort] ?? 'info';
  }

  public getImpactSeverity(score?: string): 'danger' | 'warning' | 'success' | 'secondary' {
    const map: Record<string, 'danger' | 'warning' | 'success'> = {
      HIGH: 'danger',
      MEDIUM: 'warning',
      LOW: 'success',
    };

    if (!score || !map[score]) {
      return 'secondary';
    }

    return map[score];
  }

  public formatPercent(value?: number): string {
    if (value === null || value === undefined) {
      return '—';
    }

    return `${(value * 100).toFixed(1)}%`;
  }

  public formatDurationSeconds(value?: number): string {
    if (value === null || value === undefined) {
      return '—';
    }

    const rounded = Math.round(value);
    return `${rounded}s`;
  }

  public hasGaContext(): boolean {
    return !!this.opportunity?.gaContext;
  }

  public isGaConnected(): boolean {
    return this.opportunity?.gaContext?.isGaConnected === true;
  }

  public async regenerateSummary(): Promise<void> {
    if (!this.opportunity) {
      return;
    }
    this.isRegeneratingLocal = true;
    try {
      const { data, error } = await this.apiService.regenerateSummary(this.opportunity.id, this.opportunity.projectId);
      if (error) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to regenerate summary' });
        return;
      }
      if (data) {
        this.opportunity = data;
        this.summaryRegenerated.emit(data);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Summary generated' });
      }
    } finally {
      this.isRegeneratingLocal = false;
    }
  }

  public isSummaryFailed(): boolean {
    return this.opportunity?.aiSummaryStatus === 'FAILED';
  }

  public getTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      CTR_LIFT: 'CTR Lift',
      NEAR_PAGE_ONE: 'Near Page 1',
      CONTENT_GAP: 'Content Gap',
      CANNIBALIZATION: 'Cannibalization',
      DECAY: 'Decay',
      BACKLINK_GAP: 'Backlink Gap',
      TECHNICAL_FIX: 'Technical Fix',
      LOCAL: 'Local SEO',
      REVIEW_REPUTATION_RISK: 'Review Reputation Risk',
      REVIEW_RATING_DECLINE: 'Review Rating Decline',
      COMPETITOR_CONTENT_GAP: 'Competitor Content Gap',
      LINK_INTERSECT: 'Link Intersect',
      COMPETITOR_RANK_LOSS: 'Competitor Rank Loss',
      TRAFFIC_QUALITY_LEAK: 'Quality Leak',
      CAMPAIGN_HALO: 'Campaign Halo',
      BUSINESS_IMPACT_DECAY: 'Impact Decay',
    };
    return labels[type] ?? type;
  }

  public getTypeBadgeClass(type: string): string {
    const severityMap: Record<string, 'green' | 'orange' | 'red'> = {
      CTR_LIFT: 'green',
      NEAR_PAGE_ONE: 'green',
      LOCAL: 'green',
      REVIEW_RATING_DECLINE: 'orange',
      CONTENT_GAP: 'orange',
      CANNIBALIZATION: 'orange',
      DECAY: 'orange',
      BACKLINK_GAP: 'orange',
      COMPETITOR_CONTENT_GAP: 'orange',
      LINK_INTERSECT: 'orange',
      TECHNICAL_FIX: 'red',
      REVIEW_REPUTATION_RISK: 'red',
      COMPETITOR_RANK_LOSS: 'red',
      TRAFFIC_QUALITY_LEAK: 'red',
      CAMPAIGN_HALO: 'red',
      BUSINESS_IMPACT_DECAY: 'red',
    };

    return `badge ${severityMap[type] ?? 'orange'}`;
  }

  public getEffortBadgeClass(effort: string): string {
    const map: Record<string, 'green' | 'orange' | 'red'> = {
      LOW: 'green',
      MEDIUM: 'orange',
      HIGH: 'red',
    };

    return `badge ${map[effort] ?? 'orange'}`;
  }

  public getImpactBadgeClass(score?: string): string {
    const map: Record<string, 'green' | 'orange' | 'red'> = {
      LOW: 'green',
      MEDIUM: 'orange',
      HIGH: 'red',
    };

    return `badge ${map[score ?? ''] ?? 'orange'}`;
  }

  public getStatusLabel(status: OpportunityStatus | null): string {
    const match = this.statusOptions.find((option) => option.value === status);
    return match?.label ?? '—';
  }

  public getEvidenceSourceLabel(source: string): string {
    const labels: Record<string, string> = {
      GSC: 'Search Console',
      GA4: 'Google Analytics',
      SITE_AUDIT: 'Site Audit',
      BACKLINKS: 'Backlinks',
      REVIEW_RUN: 'Review History',
      REVIEW_AI_SUMMARY: 'Review Summary',
      LOCAL_SEARCH: 'Local Search',
      COMPETITORS: 'Competitors',
    };

    return labels[source] ?? source;
  }
}
